import React, { useMemo, useRef } from 'react';
import { FiCalendar, FiChevronLeft, FiMousePointer, FiArrowUp, FiArrowDown, FiEdit3, FiDownload } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { EmptyState } from '@/components/custom/empty-state';
import EssayCover from 'src/app/images/placeholders/show-course.jpg';
import { FilterSelectNative } from '@/components/custom/filter';
import EssayStudentCard from './EssayStudentCard';
import toast from 'react-hot-toast';
import { useEffect } from 'react';
import { useState } from 'react';
import { useClient } from 'jsonapi-react';
import { Loader } from '@/components/custom/loader';
import moment from 'moment';
import Breadcrumbs from '@/components/custom/breadcrumbs/breadcrumbs';
import { statusBadgeColor, statusBadgeText } from '../utils/BadgeEssay';
import ActionCableConsumer from 'src/app/utils/actionCableConsumer';
import { useSession } from 'src/app/hooks/useSession';
import TabNavBar from './TabNavBar';
import Select from 'react-select';
import MobileEssayStudentCard from './MobileEssayStudentCard';
import { useInfiniteScroll } from '@/app/hooks/useInfiniteScroll';
import EndScroll from '@/app/components/endScroll';
import { useCheckIncludeProfiles } from '@/app/hooks/useAuthorityLevel';

export function EssayMonitoring({ essayId, uri }) {
  const [essay, setEssay] = useState({});
  const [loading, setLoading] = useState(false);
  const [webhookNotification, setWebhookNotification] = useState(null);
  const [selectedTab, setSelectedTab] = useState('redacoes');
  const [institutions, setInstitutions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const { session } = useSession();
  const isStudent = useCheckIncludeProfiles({ only: ['student'] });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const client = useClient();

  const filters = useFormik({
    initialValues: {
      status: 'blank',
      searchTerm: '',
      institution: null,
      course: null,
      classroom: null
    }
  });

  useEffect(() => {
    if (isStudent) {
      window.location.href = `/redacao/sobre/${essayId}`;
    }
  }, []);

  // Memoiza os filtros para evitar re-criação a cada render
  const memoizedFilters = useMemo(
    () => ({
      ...(filters.values.institution?.value &&
        filters.values.institution.value !== 'blank' && {
          institution_id: filters.values.institution.value
        }),
      ...(filters.values.course?.value &&
        filters.values.course.value !== 'blank' && {
          course_id: filters.values.course.value
        }),
      ...(filters.values.classroom?.value &&
        filters.values.classroom.value !== 'blank' && {
          classroom_id: filters.values.classroom.value
        }),
      ...(filters.values.status &&
        filters.values.status !== 'blank' && {
          status: filters.values.status
        })
    }),
    [filters.values.institution?.value, filters.values.course?.value, filters.values.classroom?.value, filters.values.status]
  );

  // Hook de infinite scroll com paginação virtual
  const {
    data: essayStudents,
    loading: dataLoading,
    initialLoading,
    hasMore,
    loadingNextPage, // Estado para mostrar loader ao carregar próxima página
    setData: setEssayStudents,
    sentinelRef
  } = useInfiniteScroll({
    endpoint: `essays/${essayId}/essay_students`,
    pageSize: 50,
    filters: memoizedFilters,
    searchTerm: filters.values.searchTerm,
    searchFields: ['user.name', 'user.email'],
    debounceMs: 300,
    onError: () => toast.error('Erro ao buscar estudantes')
  });

  const getEssay = async () => {
    const { data, error } = await client.fetch(['essays', essayId]);
    if (error) {
      toast.error('Erro ao buscar redação');
    } else {
      setEssay(data);
    }
  };

  const fetchInstitutions = async (url = '/institutions') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar instituições');
    } else {
      setInstitutions(data.map(item => ({ value: item.id, label: item.name })));
    }
  };

  const fetchCourses = async (url = '/courses/simple') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar cursos');
    } else {
      setCourses(data.map(item => ({ value: item.id, label: item.title })));
    }
  };

  const fetchClassrooms = async (url = '/classrooms/simple') => {
    if (filters.values.course !== null && filters.values.course.value !== 'blank' && filters.values.institution !== null && filters.values.institution.value !== 'blank') {
      let endpoint = `/classrooms/simple?filter[course_id]=${filters?.values?.course?.value}&filter[institution_id]=${filters?.values?.institution?.value}`;

      const { data, error } = await client.fetch(endpoint);
      if (error) {
        toast.error('Falha ao carregar turmas');
      } else {
        setClassrooms(data.map(item => ({ value: item.id, label: item.title })));
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([getEssay(), fetchInstitutions(), fetchCourses()]).finally(() => {
      setLoading(false);
    });
  }, []);

  const options = [
    { name: 'Correção por Professor', id: 'reviewed' },
    { name: 'Correção por IA', id: 'unreviewed' },
    { name: 'Em revisão', id: 'reviewing' },
    { name: 'Em processamento', id: 'processing' },
    { name: 'Anulada pela IA', id: 'annulled' },
    { name: 'Anulada pelo professor', id: 'annulled_by_teacher' },
    { name: 'Aluno não enviou', id: 'not_sent' },
    { name: 'Aceitando respostas', id: 'accepting_proposal' },
    { name: 'Arquivo inválido', id: 'minimum_length_not_satisfied' },
    { name: 'Falha ao processar', id: 'failed_to_process' },
    { name: 'Prazo encerrado', id: 'close_for_submissions' },
    { name: 'Aguardando envio do texto', id: 'ocr_review' }
  ];

  const breadCrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/redacao',
        name: 'Redação',
        isActive: false
      },
      {
        route: uri,
        name: essay?.topic?.length > 70 ? essay?.topic?.slice(0, 70) + '...' : essay?.topic,
        isActive: true
      }
    ]
  };

  useEffect(() => {
    if (!webhookNotification) return;

    const setNewState = (item, socket) => {
      if ((socket?.id && item?.id === socket?.id) || item?.user?.id === socket?.user_id) {
        return { ...item, status: socket?.status, corrector: socket?.corrector, grade: socket?.grade, id: socket?.id };
      }
      return item;
    };

    // CORREÇÃO: Usa função callback para acessar TODOS os dados (allData do hook)
    // prevAllData contém os 22.529 registros completos, não apenas os exibidos
    // Isso evita sobrescrever o array completo com apenas os dados paginados
    setEssayStudents(prevAllData => {
      console.log('[WebSocket] Atualizando registro via WebSocket. Total de registros:', prevAllData.length);
      return prevAllData.map(item => setNewState(item, webhookNotification));
    });
    setWebhookNotification(null);
  }, [webhookNotification, setEssayStudents]);

  // const filteredUsersByName = essayStudents.filter(item => {
  //   if (filters.values.searchTerm === '') return essayStudents;
  //   return item.user.name.toLowerCase().includes(filters.values.searchTerm.toLowerCase());
  // });

  const handleReceivedNotification = data => {
    setWebhookNotification(data);
  };

  const handleSort = key => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = useMemo(() => {
    const sortableUsers = [...essayStudents];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'name':
            aValue = a.user.name ?? '';
            bValue = b.user.name ?? '';
            break;
          case 'entrega':
            aValue = new Date(a['delivery-date']) ?? new Date(0);
            bValue = new Date(b['delivery-date']) ?? new Date(0);
            break;
          case 'status':
            aValue = a.status ?? '';
            bValue = b.status ?? '';
            break;
          case 'nota':
            aValue = a.grade ?? 0;
            bValue = b.grade ?? 0;
            break;
          case 'corrigidoPor':
            aValue = a?.corrector?.name ?? '';
            bValue = b?.corrector?.name ?? '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableUsers;
  }, [sortConfig, essayStudents]);

  useEffect(() => {
    fetchClassrooms();
  }, [filters.values.course]);

  function downloadFile() {
    const url = `${process.env.REACT_APP_DEV_URL}/reports/essays/${essayId}/report.xlsx?`;
    let params = new URLSearchParams();
    if (filters.values.classroom) {
      params.set('classroom_id', filters.values.classroom.value);
    }
    if (filters.values.institution) {
      params.set('institution_id', filters.values.institution.value);
    }
    if (filters.values.course) {
      params.set('course_id', filters.values.course.value);
    }

    fetch(`${url}${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${session?.user?.authToken}`
      }
    })
      .then(response => {
        if (!response.ok) {
          toast.error(`Erro ao baixar o relatório`);
        }
        return response.blob();
      })
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `detalhamento-${essay?.topic}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch(error => console.error('Erro no download:', error));
  }

  return (
    <div className="main-content main-content--block">
      <Breadcrumbs data={breadCrumbs} />
      <ActionCableConsumer
        channel="EssayStudentChannel"
        setObjectChannel={handleReceivedNotification}
        channelKey={'essay_id'}
        channelId={essayId}
      />

      <div className="course-about mb-6">
        <div
          className="show-course-banner"
          style={{
            backgroundImage: `url(${null || EssayCover})`
          }}
        >
          <div className="show-course-banner__inner">
            <div className="show-course-banner__container">
              <span className={`badge badge--tiny ${moment(essay?.['publish-at']).isAfter(moment()) ? 'badge--warning' : statusBadgeColor[essay.status]}`}>
                {moment(essay?.['publish-at']).isAfter(moment()) ? 'Agendada' : statusBadgeText[essay.status]}
              </span>
              <div
                className="show-course-banner__title"
                title={essay?.topic}
              >
                {essay?.topic?.length > 70 ? essay?.topic?.slice(0, 70) + '...' : essay?.topic}
              </div>
            </div>
          </div>
        </div>

        <div className="widget-wrapper mb-0">
          <div className="info-widget info-widget--t3">
            <div className="info-widget__header">
              <h3 className="info-widget__title">Índice de envio</h3>
            </div>

            <div className="info-widget__body">
              <p className="info-widget__value">
                <FiMousePointer /> {essay?.['submissions-percent']}
              </p>
            </div>
          </div>

          <div className="info-widget info-widget--t3">
            <div className="info-widget__header">
              <h3 className="info-widget__title">Nota média</h3>
            </div>

            <div className="info-widget__body">
              <p className="info-widget__value">
                <FiCalendar /> {essay?.['average-grade']}
              </p>
            </div>
          </div>

          <div className="info-widget info-widget--t3">
            <div className="info-widget__header">
              <h3 className="info-widget__title">Prazo de entrega</h3>
            </div>

            <div className="info-widget__body">
              <p className="info-widget__value">
                <FiCalendar /> {moment(essay?.['delivery-date']).format('DD/MM/YYYY')}
              </p>
            </div>
          </div>

          {essay?.['allow-multiple-submissions'] && (
            <div className="info-widget info-widget--t3">
              <div className="info-widget__header">
                <h3 className="info-widget__title">Envio por estudante</h3>
              </div>

              <div className="info-widget__body">
                <p className="info-widget__value">
                  {/* Aguardar definição da regra de quantidade de envios */}
                  <FiEdit3 /> 2 envios
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="course-about__body">
          <div>
            <h3 className="course-about__title mb-1">Envios</h3>
            <div className="card__progress card__progress--value-first mb-0">
              <div className="card__progress-container">
                <div
                  className="card__progress-bar"
                  role="progressbar"
                  style={{ width: `${essay?.['submissions-percent']}` }}
                  aria-valuenow={essay?.['submissions-percent']}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
              <div className="card__progress-text">{`${essay?.['submissions-count'] >= 0 ? essay?.['submissions-count'] : '--'}/${essay?.['total-of-students'] >= 0 ? essay?.['total-of-students'] : '--'} `}</div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="mb-6"
        style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}
      >
        <TabNavBar
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
        <button
          type="button"
          className="btn btn--outline"
          onClick={downloadFile}
        >
          <FiDownload className="btn__icon" /> Exportar relatório detalhado por aluno
        </button>
      </div>

      {selectedTab === 'redacoes' && (
        <div className="course-about">
          <div className="course-about__body">
            <div className="course-about__filter">
              <div className="filter-bar filter-bar--borderless">
                <div className="filter-bar__inner">
                  <div className="filter-bar__row">
                    <label
                      htmlFor="search"
                      className="filter-bar__label"
                    >
                      {t('textsCommon.search')}
                    </label>
                    <input
                      className="form__control form__control--search-with-icon"
                      placeholder="Pesquisar por estudante"
                      type="search"
                      name="search"
                      id="search"
                      onChange={e => filters.setFieldValue('searchTerm', e.target.value)}
                    />
                  </div>

                  <FilterSelectNative
                    blankLabel={t('filter.blankLabelAll2')}
                    label={t('filter.labelSelectStatus')}
                    value={filters.values.status}
                    onChange={e => filters.setFieldValue('status', e.target.value)}
                    options={options}
                  />

                  {/* Instituição - DESABILITADO: A API retorna institution: {id: null, name: null} */}
                  <div className="filter-bar__row">
                    <label
                      htmlFor="search"
                      className="filter-bar__label"
                    >
                      Instituição
                    </label>
                    <Select
                      value={filters.values.institution ?? { value: 'blank', label: 'Todos' }}
                      openMenuOnFocus
                      options={[{ value: 'blank', label: 'Todos' }, ...institutions]}
                      className="react-multi-select filter-bar__multi-select"
                      classNamePrefix="react-multi-select"
                      placeholder="Dados não disponíveis"
                      noOptionsMessage={() => 'Sem opções'}
                      components={{
                        IndicatorSeparator: () => null,
                        ClearIndicator: () => null
                      }}
                      onChange={selectedOption => {
                        // Normalizar: 'blank' → null para estado limpo
                        const normalizedValue = selectedOption?.value === 'blank' ? null : selectedOption;
                        filters.setFieldValue('institution', normalizedValue);
                        // Limpar filtros dependentes
                        filters.setFieldValue('course', null);
                        filters.setFieldValue('classroom', null);
                      }}
                    />
                  </div>

                  {/* Curso - DESABILITADO: A API não retorna o campo 'course' */}
                  <div className="filter-bar__row">
                    <label
                      htmlFor="search"
                      className="filter-bar__label"
                    >
                      Curso
                    </label>
                    <Select
                      value={filters.values.course ?? { value: 'blank', label: 'Todos' }}
                      openMenuOnFocus
                      options={[{ value: 'blank', label: 'Todos' }, ...courses]}
                      className="react-multi-select filter-bar__multi-select"
                      classNamePrefix="react-multi-select"
                      placeholder="Dados não disponíveis"
                      noOptionsMessage={() => 'Sem opções'}
                      components={{
                        IndicatorSeparator: () => null,
                        ClearIndicator: () => null
                      }}
                      onChange={selectedOption => {
                        // Normalizar: 'blank' → null para estado limpo
                        const normalizedValue = selectedOption?.value === 'blank' ? null : selectedOption;
                        filters.setFieldValue('course', normalizedValue);
                        // Limpar filtro dependente
                        filters.setFieldValue('classroom', null);
                      }}
                    />
                  </div>

                  {/* Turma - DESABILITADO: A API não retorna o campo 'classroom' */}
                  <div className="filter-bar__row">
                    <label
                      htmlFor="search"
                      className="filter-bar__label"
                    >
                      Turma
                    </label>
                    <Select
                      value={filters.values.classroom ?? { value: 'blank', label: 'Todos' }}
                      openMenuOnFocus
                      options={[{ value: 'blank', label: 'Todos' }, ...classrooms]}
                      className="react-multi-select filter-bar__multi-select"
                      classNamePrefix="react-multi-select"
                      placeholder="Dados não disponíveis"
                      noOptionsMessage={() => 'Sem opções'}
                      components={{
                        IndicatorSeparator: () => null,
                        ClearIndicator: () => null
                      }}
                      onChange={selectedOption => {
                        // Normalizar: 'blank' → null para estado limpo
                        const normalizedValue = selectedOption?.value === 'blank' ? null : selectedOption;
                        filters.setFieldValue('classroom', normalizedValue);
                      }}
                    />
                  </div>
                </div>
              </div>
              {initialLoading ? (
                <Loader />
              ) : !dataLoading && sortedUsers.length === 0 ? (
                <EmptyState
                  type="content"
                  title={t('emptyState.noItemFound')}
                />
              ) : (
                <div>
                  {/* Cards mobile */}
                  <div
                    className="hide-md"
                    style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
                  >
                    {sortedUsers?.map(student => (
                      <MobileEssayStudentCard
                        currentUser={session.user.id}
                        essay={essay}
                        key={student?.user?.id}
                        student={student}
                      />
                    ))}
                  </div>

                  {/* Tabela desktop */}
                  <div className="round-dg-wrapper show-md">
                    <table
                      className="round-dg round-dg--light"
                      style={{ minWidth: '1200px' }}
                    >
                      <thead className="round-dg__header">
                        <tr className="round-dg__row">
                          <th
                            className="round-dg__cell-header"
                            onClick={() => handleSort('name')}
                            style={{ cursor: 'pointer', fontWeight: sortConfig.key === 'name' ? 'bold' : 'normal' }}
                          >
                            Nome {sortConfig.key === 'name' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                          </th>
                          <th
                            className="round-dg__cell-header"
                            style={{ cursor: 'pointer', width: '80px' }}
                          >
                            Envios {sortConfig.key === 'envios' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                          </th>
                          <th
                            className="round-dg__cell-header"
                            style={{ cursor: 'pointer', width: '116px', fontWeight: sortConfig.key === 'entrega' ? 'bold' : 'normal' }}
                            onClick={() => handleSort('entrega')}
                          >
                            Entrega {sortConfig.key === 'entrega' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                          </th>
                          <th
                            className="round-dg__cell-header"
                            style={{ cursor: 'pointer', width: '82px', fontWeight: sortConfig.key === 'nota' }}
                            onClick={() => handleSort('nota')}
                          >
                            Nota {sortConfig.key === 'nota' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                          </th>
                          <th
                            className="round-dg__cell-header"
                            style={{ cursor: 'pointer', width: '180px', fontWeight: sortConfig.key === 'status' ? 'bold' : 'normal' }}
                            onClick={() => handleSort('status')}
                          >
                            Status da correção {sortConfig.key === 'status' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                          </th>
                          <th
                            className="round-dg__cell-header"
                            style={{ cursor: 'pointer', width: '226px', fontWeight: sortConfig.key === 'corrigidoPor' ? 'bold' : 'normal' }}
                            onClick={() => handleSort('corrigidoPor')}
                          >
                            Corrigido por {sortConfig.key === 'corrigidoPor' && (sortConfig.direction === 'ascending' ? <FiArrowUp /> : <FiArrowDown />)}
                          </th>
                          <th
                            className="round-dg__cell-header"
                            style={{ width: '370px' }}
                          >
                            Ações
                          </th>
                        </tr>
                      </thead>

                      <tbody className="round-dg__body">
                        {sortedUsers?.map((student, i) => (
                          <EssayStudentCard
                            currentUser={session.user.id}
                            essay={essay}
                            key={i}
                            student={student}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Elemento sentinela para Intersection Observer */}
                  <div
                    ref={sentinelRef}
                    style={{
                      height: '20px',
                      margin: '20px 0',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {(dataLoading || loadingNextPage) && <Loader />}
                  </div>

                  {/* Mensagem de fim */}
                  {!hasMore && !loadingNextPage && sortedUsers.length > 0 && <EndScroll />}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'sobre' && (
        <div className="course-about">
          <div className="course-about__header">
            <h1 className="course-about__title">Enunciado</h1>

            <p
              className="course-about__description"
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {essay?.statement}
            </p>

            <p className="course-about__description">
              Tema: {essay?.topic}
              <br />
              {/* Título: <strong>{essay?.title}</strong> */}
            </p>
          </div>

          <div className="course-about__body">
            <h2 className="course-about__title">Textos motivadores</h2>
            {essay?.['essay-motivator-texts']?.map((item, index) => {
              return (
                <article>
                  <h3 className="course-about__subtitle">Texto {index + 1}</h3>
                  <p
                    className="course-about__description"
                    dangerouslySetInnerHTML={{ __html: item.body }}
                  />
                  {/* <p className="course-about__description">Disponível em: https://brasilescola.uol.com.br/portugues/preconceito-linguistico.htm. Acesso em: 19 ago. 2022 (adaptado).</p> */}
                </article>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
